import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Package, MapPin, Truck, ShieldCheck, ArrowRight, Instagram, Facebook } from "lucide-react";
import { SectionHeading } from "../../components/shared/SectionHeading";
import { ProductCard } from "../../components/shared/ProductCard";
import { IMAGES, mapApiProduct } from "../../data/mockData";
import { usePageMedia } from "../../lib/usePageMedia";
import { usePageTexts } from "../../lib/usePageTexts";
import { ReviewsSection } from "../../components/shared/ReviewsSection";
import { api, formatApiError } from "../../lib/api";

const REASSURANCE = [
  { icon: Award, title: "6 ans d'expérience" },
  { icon: Package, title: "Matériel appartenant à Art Event" },
  { icon: MapPin, title: "Retrait gratuit à Artres" },
  { icon: Truck, title: "Livraison jusqu'à 80 km" },
  { icon: ShieldCheck, title: "Réservation simple & sécurisée" },
];

const UNIVERS = [
  { title: "Coordination de mariage", text: "Profitez pleinement de vos préparatifs. Nous vous conseillons, vous accompagnons et, selon la formule choisie, coordonnons votre journée.", cta: "Découvrir nos formules", to: "/coordination", image: IMAGES.coordination, slot: "accueil:coordination" },
  { title: "Décoration & mobilier", text: "Composez une réception à votre image parmi notre sélection de décoration et de mobilier disponibles à la location.", cta: "Découvrir la collection", to: "/boutique", image: IMAGES.decoration, slot: "accueil:decoration" },
  { title: "Jeux en bois", text: "Créez des moments de partage avec une sélection de jeux en bois qui séduira petits et grands.", cta: "Découvrir les jeux", to: "/boutique/jeux-en-bois", image: IMAGES.jeux, slot: "accueil:jeux-bois" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.55, ease: "easeOut" } }),
};

export default function HomePage() {
  const pageMedia = usePageMedia();
  const { text } = usePageTexts();
  const [favorites, setFavorites] = useState(null);
  const [realisations, setRealisations] = useState(null);
  const [partners, setPartners] = useState(null);
  const [social, setSocial] = useState(null);
  useEffect(() => {
    api.get("/site-settings").then((r) => setSocial(r.data)).catch(() => setSocial({}));
  }, []);
  const [contact, setContact] = useState({ first_name: "", name: "", email: "", phone: "", subject: "Mariage", event_date: "", event_date_tbd: false, message: "", company: "" });
  const [contactState, setContactState] = useState("idle"); // idle | sending | sent
  const [contactError, setContactError] = useState("");

  const submitContact = async (e) => {
    e.preventDefault();
    setContactState("sending");
    setContactError("");
    try {
      await api.post("/contact", {
        first_name: contact.first_name, name: contact.name, email: contact.email, phone: contact.phone,
        subject: contact.subject, event_date: contact.event_date || null, event_date_tbd: contact.event_date_tbd,
        message: contact.message, source: "accueil", company: contact.company,
      });
      setContactState("sent");
    } catch (err) {
      setContactState("idle");
      setContactError(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    api.get("/catalog/products", { params: { favorites: true } })
      .then((r) => setFavorites(r.data.map(mapApiProduct).slice(0, 4)))
      .catch(() => setFavorites([]));
    api.get("/gallery")
      .then((r) => setRealisations(r.data.slice(0, 3)))
      .catch(() => setRealisations([]));
    api.get("/partners")
      .then((r) => setPartners(r.data))
      .catch(() => setPartners([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative flex min-h-[78vh] items-center overflow-hidden" data-testid="home-hero">
        <img src={pageMedia["accueil:hero"]?.url || IMAGES.hero} alt={pageMedia["accueil:hero"]?.alt || "Art Event — décoration événementielle"} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-deepgreen/45" />
        <div className="container-ae relative py-24">
          <motion.p initial="hidden" animate="visible" variants={fadeUp} className="font-accent text-3xl text-gold-light">
            Art Event
          </motion.p>
          <motion.h1
            initial="hidden" animate="visible" custom={1} variants={fadeUp}
            className="mt-4 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight text-white"
            data-testid="home-hero-title"
          >
            {text("accueil:hero-titre", "Imaginez votre événement.||Nous créons le reste.").split("||").map((l, i) => <span key={i}>{l}{i === 0 && <br />}</span>)}
          </motion.h1>
          <motion.p initial="hidden" animate="visible" custom={2} variants={fadeUp} className="mt-6 max-w-xl text-lg text-white/85 leading-relaxed">
            {text("accueil:hero-accroche", "Décoration, mobilier, jeux en bois et coordination de mariage : imaginez votre journée, Art Event vous accompagne pour lui donner vie.")}
          </motion.p>
          <motion.div initial="hidden" animate="visible" custom={3} variants={fadeUp} className="mt-9 flex flex-wrap gap-4">
            <Link to="/rendez-vous" data-testid="home-cta-appointment" className="btn-primary-ae !bg-gold !text-deepgreen hover:!bg-gold-hover">
              Prendre rendez-vous
            </Link>
            <Link to="/boutique" data-testid="home-cta-catalog" className="btn-secondary-ae !border-white/50 !text-white hover:!bg-white hover:!text-deepgreen">
              Découvrir nos locations
            </Link>
          </motion.div>
        </div>
      </section>

      {/* RÉASSURANCE */}
      <section className="border-b border-border bg-white" data-testid="home-reassurance">
        <div className="container-ae grid grid-cols-2 gap-6 py-10 sm:grid-cols-3 lg:grid-cols-5">
          {REASSURANCE.map((item) => (
            <div key={item.title} className="flex items-center gap-3" data-testid={`reassurance-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <item.icon className="h-6 w-6 shrink-0 text-sage" aria-hidden />
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-deepgreen leading-snug">{item.title}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3 UNIVERS */}
      <section className="py-24 md:py-32" data-testid="home-univers">
        <div className="container-ae">
          <SectionHeading overline="Ce que nous faisons pour vous" title="Un seul partenaire," accent="de multiples possibilités" />
          <div className="grid gap-8 md:grid-cols-3">
            {UNIVERS.map((u, i) => (
              <motion.div key={u.title} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}>
                <Link to={u.to} data-testid={`universe-${u.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group block">
                  <div className="relative overflow-hidden rounded-xl">
                    <img src={pageMedia[u.slot]?.url || u.image} alt={pageMedia[u.slot]?.alt || u.title} loading="lazy" className="aspect-[3/4] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-deepgreen/70 via-deepgreen/10 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-6">
                      <h3 className="font-heading text-2xl text-white">{u.title}</h3>
                      <p className="mt-2 text-sm text-white/80">{u.text}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-gold-light">
                        {u.cta} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COUPS DE CŒUR — catalogue réel (produits actifs marqués is_favorite, max 4) */}
      {favorites?.length > 0 && (
        <section className="bg-white py-24 md:py-28" data-testid="home-favorites">
          <div className="container-ae">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <SectionHeading overline="La boutique" title="Nos coups de cœur" />
              <Link to="/boutique" data-testid="home-see-collection" className="btn-secondary-ae mb-10 md:mb-14 !px-5 !py-2.5 !text-xs">
                Voir toute la collection <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
              {favorites.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RÉALISATIONS — CMS Réalisations (3 premières publiées) */}
      {realisations?.length > 0 && (
        <section className="py-24 md:py-32" data-testid="home-realisations">
          <div className="container-ae">
            <SectionHeading overline="Nos réalisations" title="Des événements" accent="qui vous ressemblent" />
            <div className="grid gap-6 md:grid-cols-3">
              {realisations.map((r, i) => (
                <motion.div key={r.slug} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp} className={i === 1 ? "md:mt-12" : ""}>
                  <Link to={`/realisations/${r.slug}`} data-testid={`realisation-${r.slug}`} className="group block">
                    <div className="overflow-hidden rounded-xl">
                      <img src={r.cover_image || r.images?.[0]} alt={r.title} loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="mt-4">
                      {(r.tags || []).length > 0 && <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{r.tags.join(" • ")}</p>}
                      <h3 className="mt-1.5 font-heading text-xl text-deepgreen">{r.title}</h3>
                      {r.place && <p className="text-sm text-muted-foreground">{r.place}</p>}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="mt-12 text-center">
              <Link to="/realisations" data-testid="home-see-realisations" className="btn-secondary-ae">
                Voir toutes les réalisations
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* AVIS CLIENTS (CMS Avis — placement « accueil ») */}
      <ReviewsSection placement="accueil" />

      {/* PARTENAIRES (CMS Partenaires — grille dynamique) */}
      <section className="py-20 md:py-24" data-testid="home-partners">
        <div className="container-ae">
          {partners?.length > 0 && (
            <>
              <div className="text-center">
                <p className="overline-ae mb-3">Nos partenaires</p>
                <h2 className="text-3xl sm:text-4xl text-deepgreen">Des professionnels de confiance</h2>
              </div>
              <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-partners-grid">
                {partners.map((p) => {
                  const key = p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  const card = (
                    <div className="card-ae h-full p-6 text-center transition-transform duration-300 hover:-translate-y-1" data-testid={`partner-${key}`}>
                      {p.logo ? (
                        <img src={p.logo} alt={p.name} loading="lazy" className="mx-auto h-16 w-16 rounded-xl border border-border bg-white object-contain p-1.5" />
                      ) : (
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-xl bg-sage-light font-heading text-2xl text-deepgreen">{p.name.charAt(0)}</div>
                      )}
                      <h3 className="mt-4 font-heading text-lg text-deepgreen">{p.name}</h3>
                      {p.category && <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{p.category}</p>}
                      {p.location && <p className="mt-1 flex items-center justify-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3 text-sage" /> {p.location}</p>}
                    </div>
                  );
                  return p.link ? (
                    <a key={p.id || key} href={p.link} target="_blank" rel="noreferrer" className="block">{card}</a>
                  ) : (
                    <div key={p.id || key}>{card}</div>
                  );
                })}
              </div>
              <p className="mt-8 text-center">
                <Link to="/partenaires" className="btn-secondary-ae !px-5 !py-2.5 !text-xs" data-testid="home-partners-more">Tous nos partenaires</Link>
              </p>
            </>
          )}
          {(social?.instagram_url || social?.facebook_url) && (
            <div className="mt-14 flex items-center justify-center gap-4">
              {social.instagram_url && (
                <a href={social.instagram_url} target="_blank" rel="noreferrer" data-testid="home-instagram" className="btn-secondary-ae !px-5 !py-2.5 !text-xs">
                  <Instagram className="h-4 w-4" /> Instagram
                </a>
              )}
              {social.facebook_url && (
                <a href={social.facebook_url} target="_blank" rel="noreferrer" data-testid="home-facebook" className="btn-secondary-ae !px-5 !py-2.5 !text-xs">
                  <Facebook className="h-4 w-4" /> Facebook
                </a>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CONTACT */}
      <section className="bg-deepgreen py-24 md:py-28 text-cream" data-testid="home-contact">
        <div className="container-ae grid items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="overline-ae !text-gold mb-3">Contact</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight">Parlez-nous de votre événement</h2>
            <p className="mt-5 max-w-md text-cream/75 leading-relaxed">
              Une question, un projet ? Écrivez-nous — nous répondons du lundi au samedi, de 9 h à 18 h.
            </p>
            <p className="mt-6 text-sm text-cream/60">contact@art-events.fr — 06 88 60 83 53</p>
          </div>
          {contactState === "sent" ? (
            <div className="rounded-xl bg-white/10 p-6" data-testid="home-contact-success">
              <p className="font-heading text-xl">Message envoyé</p>
              <p className="mt-2 text-sm text-cream/80">
                Merci {contact.name} — votre message est bien enregistré. Art Event vous répond du lundi au samedi, de 9 h à 18 h.
              </p>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={submitContact} data-testid="home-contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <input className="input-ae" placeholder="Votre prénom *" required minLength={1} value={contact.first_name} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} data-testid="home-contact-first-name" aria-label="Votre prénom" />
                <input className="input-ae" placeholder="Votre nom *" required minLength={2} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="home-contact-name" aria-label="Votre nom" />
                <input className="input-ae" type="email" placeholder="Votre e-mail *" required value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="home-contact-email" aria-label="Votre e-mail" />
                <input className="input-ae" type="tel" placeholder="Votre téléphone *" required minLength={6} value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="home-contact-phone" aria-label="Votre téléphone" />
                <select className="input-ae" value={contact.subject} onChange={(e) => setContact({ ...contact, subject: e.target.value })} data-testid="home-contact-subject" aria-label="Type d'événement">
                  <option>Mariage</option>
                  <option>Location de décoration / matériel</option>
                  <option>Coordination de mariage</option>
                  <option>Anniversaire / fête privée</option>
                  <option>Événement d'entreprise</option>
                  <option>Autre demande</option>
                </select>
                <div>
                  <input className="input-ae" type="date" required={!contact.event_date_tbd} disabled={contact.event_date_tbd} value={contact.event_date} onChange={(e) => setContact({ ...contact, event_date: e.target.value })} data-testid="home-contact-date" aria-label="Date de l'événement" />
                  <label className="mt-1.5 flex items-center gap-2 text-xs text-cream/70">
                    <input type="checkbox" checked={contact.event_date_tbd} onChange={(e) => setContact({ ...contact, event_date_tbd: e.target.checked })} className="h-4 w-4 accent-[#D4AF37]" data-testid="home-contact-date-tbd" />
                    Date à définir
                  </label>
                </div>
              </div>
              <textarea className="input-ae min-h-28" placeholder="Décrivez votre besoin (lieu, invités, envies…) *" required minLength={10} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} data-testid="home-contact-message" aria-label="Votre message" />
              <input type="text" name="company" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid="home-contact-company" />
              {contactError && <p className="text-sm text-gold-light" data-testid="home-contact-error">{contactError}</p>}
              <button type="submit" disabled={contactState === "sending"} data-testid="home-contact-submit" className="btn-primary-ae !bg-gold !text-deepgreen hover:!bg-gold-hover disabled:opacity-60">
                {contactState === "sending" ? "Envoi en cours…" : "Envoyer mon message"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
